<script setup lang="ts">
import { computed, onMounted, ref, Ref, watch } from 'vue';
import { useStore } from 'vuex';
import { loadFont } from './functions/makerjs';
import AccordionContainer from '@/components/accordion/AccordionContainer.vue';
import AccordionItem from '@/components/accordion/AccordionItem.vue';
import MountingShapePreview from '@/components/MountingShapePreview.vue';
import ShapeSelector from '@/components/ShapeSelector.vue';
import ShapeSettings from '@/components/ShapeSettings.vue';
import MetaInformation from '@/components/MetaInformation.vue';
import DrillHoles from '@/components/DrillHoles.vue';
import CutoutsConfiguration from '@/components/CutoutsConfiguration.vue';
import ShapeUpload from '@/components/ShapeUpload.vue';
import SideSelection from '@/components/SideSelection.vue';
import EdgeFinish from '@/components/EdgeFinish.vue';
import ThicknessSelector from '@/components/ThicknessSelector.vue';
import MaterialSelector from '@/components/MaterialSelector.vue';
import HeaderPricingInformation from '@/components/HeaderPricingInformation.vue';
import MeasurementsHeightTransition from '@/components/UI/MeasurementsHeightTransition.vue';
import InstructionsButton from '@/components/UI/InfoModal/InstructionsButton.vue';
import InfoModal from '@/components/UI/InfoModal/InfoModal.vue';
import { StepTypes } from '@/types/step';

const store = useStore();
const i18n = window.measurementSelectorVariables.i18n;
const html = window.measurementSelectorVariables.html;
const shop = window.measurementSelectorVariables.shop;
const settings = window.measurementSelectorVariables.settings;
const measuringInstructions = window.measuringInstructions;
const containerRef = ref<HTMLElement | null>(null);

const selectedUnit = 'cm';

const shape = computed(() => store.getters.shape);
const isMediaMobileSize = computed(() => store.getters.isMediaMobileSize);
const primitives = computed(() => store.getters.primitives);
const currentStepName = computed(() => store.getters.currentStepName);
const isDrillHolesVisible = computed(() => store.getters.isDrillHolesVisible);
const drillHoleEdgeType = computed(() => store.getters.drillHoleEdgeType);
const alternateThicknesses = computed(() => store.getters.alternateThicknesses);
const measurements = computed(() => store.getters.measurements);
const stringifiedConfig = computed(() => store.getters.stringifiedConfig);
const exportCompositeDxf = computed(() => store.getters.exportCompositeDxf);
const dxfExport = computed(() => store.getters.dxfExport);
const exportShape = computed(() => store.getters.exportShape);
const isShapeComplete = computed(() => store.getters.isShapeComplete);
const isEdgeFinishVisible = computed(() => store.getters.isEdgeFinishVisible);
const isCutoutsVisible = computed(() => store.getters.isCutoutsVisible);
const alternateMaterials = computed(() => store.getters.alternateMaterials);
const showSideSelection = computed(() => store.getters.showSideSelection);

function isPortalActive( stepName: StepTypes ): boolean {
	return currentStepName.value === stepName;
}

function scrollTo(view: Ref<HTMLElement | null>) {
	view.value?.scrollIntoView({ behavior: 'smooth' });
}

onMounted(async () => {

	// Pre-load default font
	const defaultFont = `${window.measurementSelectorVariables.fontDirUrl}Exo-Bold.ttf`;
	const fontObj = await loadFont(defaultFont);

	store.commit('addFont', { fontName: 'Exo-Bold', font: fontObj });
	store.commit('setSelectedFontObject', fontObj );

	window?.dataLayer?.push( {
		event: 'measurements_selector',
		eventCategory: 'Measurements Selector',
		eventAction: 'Display',
		eventLabel: document.title,
	} );
});

watch(currentStepName, async (newValue: StepTypes, oldValue: StepTypes) => {
	if (newValue !== oldValue) {
		if (!isMediaMobileSize.value) {
			scrollTo(containerRef);
		}
	}
});

</script>
<template>
	<div id="app-measurements-selector" class="measurements-selector" ref="containerRef">
		<header-pricing-information></header-pricing-information>
		<accordion-container>
			<accordion-item
				:step-name="StepTypes.shapeSize"
			>
				<shape-selector />

				<portal to="mobile-accordion-preview" :disabled="!isMediaMobileSize">
					<MountingShapePreview
						:primitives="primitives"
						:unit="selectedUnit"
					></MountingShapePreview>
				</portal>

				<portal-target name="mobile-accordion-preview" v-if="isMediaMobileSize && isPortalActive( StepTypes.shapeSize )"></portal-target>

				<shape-settings v-if="shape !== 'dxf'"/>

				<measurements-height-transition appear mode="out-in">
					<shape-upload
						v-if="shape === 'dxf'"
					/>
				</measurements-height-transition>

				<div v-if="measuringInstructions && measuringInstructions.length > 0" class="accordion-item__info-button">
					<instructions-button
						modal-name="measuringInstrictions"
						:label="i18n.measuringInstructionsLabel"
					>{{ i18n.measuringInstructionsLabel }}</instructions-button>
					<info-modal modal-name="measuringInstrictions" :content="measuringInstructions"></info-modal>
				</div>

			</accordion-item>

			<accordion-item
				v-if="shape !== '' && alternateThicknesses.length > 0"
				:step-name="StepTypes.thickness"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.thickness )" name="mobile-accordion-preview"></portal-target>

				<ThicknessSelector />

			</accordion-item>

			<accordion-item
				v-if="shape !== '' && alternateMaterials.length > 0"
				:step-name="StepTypes.material"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.material )" name="mobile-accordion-preview"></portal-target>

				<material-selector />
			</accordion-item>

			<accordion-item
				v-if="isDrillHolesVisible"
				:step-name="StepTypes.drillholes"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.drillholes ) && drillHoleEdgeType !== 'around'" name="mobile-accordion-preview"></portal-target>

				<drill-holes />
			</accordion-item>

			<accordion-item
				v-if="isCutoutsVisible"
				:step-name="StepTypes.cutouts"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.cutouts )" name="mobile-accordion-preview"></portal-target>

				<cutouts-configuration />
			</accordion-item>

			<accordion-item
				v-if="showSideSelection"
				:step-name="StepTypes.sideSelection"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.sideSelection )" name="mobile-accordion-preview"></portal-target>

				<side-selection />
			</accordion-item>

			<accordion-item
				v-if="isEdgeFinishVisible"
				:step-name="StepTypes.edgeFinish"
			>
				<portal-target v-if="isMediaMobileSize && isPortalActive( StepTypes.edgeFinish )" name="mobile-accordion-preview"
				></portal-target>
				<edge-finish />
			</accordion-item>
		</accordion-container>

		<MetaInformation
			:isShapeComplete="isShapeComplete"
			:availability="html.availability"
			:shape="measurements.shape"
			:primitives="primitives"
			:settings="settings"
			:currency="shop.currency"
			:locale="shop.locale"
			:dxfDefinition="measurements.dxfDefinition"
		/>

		<input
			v-if="measurements.modifiers.font !== ''"
			type="hidden"
			name="measurements[modifiers][font]"
			:value="measurements.modifiers.font">
		<input
			type="hidden"
			name="measurements[shape]"
			v-bind:value="measurements.shape"
		/>
		<input
			type="hidden"
			name="measurements[export_shape]"
			v-bind:value="exportShape"
		/>

		<input
			v-if="exportCompositeDxf"
			type="hidden"
			name="measurements[dxf_export]"
			:value="dxfExport"
		/>

		<input
			type="hidden"
			name="measurements_config"
			:value="stringifiedConfig"
		/>
	</div>
</template>
<style scoped lang="scss">
.measurements-selector {
	scroll-margin-block-start: calc(var(--header-height) + 70px);
}
</style>
